import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/lib/queryKeys'
import { useWorkspaces } from '@/features/workspace/hooks/useWorkspaces'
import {
  allowInWorkspace,
  createKey,
  getActivity,
  getAvailability,
  getStatus,
  getWorkspaceConnections,
  listConnections,
  removeFromWorkspace,
  replaceKey,
  resumeConnection,
  revokeConnection,
  updateConnection,
} from '../api'

/**
 * Is ChatGPT & Claude open to this person (owner-first release, plan §11.5 #9)?
 * Cached 10 minutes; any error counts as "not available" so nothing shows.
 */
export function useAiAvailability() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.aiConnectionsAvailability(),
    queryFn: getAvailability,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  })
  return {
    enabled: !isError && data?.enabled === true,
    paused: data?.paused === true,
    address: data?.address || 'https://mcp.codedesign.ai/mcp',
    isLoading,
  }
}

export function useAiConnections({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.aiConnectionsList(),
    queryFn: listConnections,
    enabled,
    staleTime: 15 * 1000,
  })
}

export function useAiConnectionActivity(id, changesOnly) {
  return useQuery({
    queryKey: queryKeys.aiConnectionActivity(id, changesOnly),
    queryFn: () => getActivity(id, changesOnly),
    enabled: Boolean(id),
  })
}

/** Polls every 3 s for a new connection (or a key's first use) since the dialog opened, for up to 10 minutes (plan §17.1 step 3). */
export function useAiConnectStatus(since, { active }) {
  const startedAt = useMemo(() => Date.parse(since || '') || Date.now(), [since])
  return useQuery({
    queryKey: queryKeys.aiConnectionsStatus(since),
    queryFn: () => getStatus(since),
    enabled: Boolean(active && since),
    refetchInterval: () => (Date.now() - startedAt < 10 * 60 * 1000 ? 3000 : false),
    refetchIntervalInBackground: true,
  })
}

export function useAiConnectionMutations() {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.aiConnections })
  return {
    update: useMutation({ mutationFn: ({ id, changes }) => updateConnection(id, changes), onSuccess: refresh }),
    revoke: useMutation({ mutationFn: (id) => revokeConnection(id), onSuccess: refresh }),
    resume: useMutation({ mutationFn: (id) => resumeConnection(id), onSuccess: refresh }),
    createKey: useMutation({ mutationFn: (choices) => createKey(choices), onSuccess: refresh }),
    replaceKey: useMutation({ mutationFn: (id) => replaceKey(id), onSuccess: refresh }),
  }
}

export function useWorkspaceAiConnections(workspaceId, { enabled = true } = {}) {
  const queryClient = useQueryClient()
  const key = queryKeys.workspaceAiConnections(workspaceId)
  const query = useQuery({ queryKey: key, queryFn: () => getWorkspaceConnections(workspaceId), enabled: Boolean(workspaceId) && enabled, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: key })
  return {
    ...query,
    remove: useMutation({ mutationFn: (connectionId) => removeFromWorkspace(workspaceId, connectionId), onSuccess: refresh }),
    allow: useMutation({ mutationFn: (connectionId) => allowInWorkspace(workspaceId, connectionId), onSuccess: refresh }),
  }
}

/** The person's workspaces as {id, name}, and a lookup for names in the table. */
export function useWorkspaceNames() {
  const { workspaces = [] } = useWorkspaces()
  return useMemo(() => {
    const list = workspaces.map((workspace) => ({ id: String(workspace._id || workspace.id), name: workspace.name || 'Workspace', isDefault: workspace.isDefault === true }))
    return { list, namesById: Object.fromEntries(list.map((workspace) => [workspace.id, workspace.name])) }
  }, [workspaces])
}
